import { Box, Container, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";

const HeroHeroSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let width = canvas.width = window.innerWidth;
    let height = canvas.height = 646; // Match the section height

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = 646;
    };

    window.addEventListener('resize', handleResize);

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 1.2;
        this.vy = (Math.random() - 0.5) * 1.2;
        this.size = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }

      draw() {
        ctx.fillStyle = 'rgba(253, 251, 247, 0.6)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const particles = [];
    const particleCount = window.innerWidth < 768 ? 40 : 60;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    let mouseX = width / 2;
    let mouseY = height / 2;

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;

      particles.forEach(particle => {
        const dx = mouseX - particle.x;
        const dy = mouseY - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 120) {
          particle.vx -= dx * 0.00008;
          particle.vy -= dy * 0.00008;
        }
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    function animate() {
      ctx.fillStyle = 'rgba(17, 17, 17, 0.1)';
      ctx.fillRect(0, 0, width, height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw connections
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 200) {
            ctx.strokeStyle = `rgba(232, 178, 152, ${0.4 * (1 - distance / 200)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: "646px",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        backgroundColor: "#111111",
      }}
    >
      {/* Animated Canvas Background */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.7,
        }}
      />

      {/* Grid Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: 
            'linear-gradient(rgba(253, 251, 247, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(253, 251, 247, 0.08) 1px, transparent 1px)',
          backgroundSize: "80px 80px",
          zIndex: 1,
        }}
      />

      {/* Geometric Accents */}
      <Box
        sx={{
          position: "absolute",
          top: "12%",
          right: "15%",
          width: "180px",
          height: "180px",
          border: "1px solid #E8B298",
          transform: "rotate(45deg)",
          opacity: 0.15,
          zIndex: 1,
          display: { xs: "none", md: "block" },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "20%",
          left: "8%",
          width: "120px",
          height: "120px",
          border: "1px solid #7A9B9E",
          transform: "rotate(45deg)",
          opacity: 0.15,
          zIndex: 1,
          display: { xs: "none", md: "block" },
        }}
      />

      {/* Content */}
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 10 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth: "641px",
            margin: "0 auto",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#E8B298",
              fontSize: "15px",
              fontFamily: "'Inter-Regular', Helvetica",
              lineHeight: "18px",
              marginBottom: 3,
              textTransform: "uppercase",
              letterSpacing: "0.2em",
              fontWeight: 600,
            }}
          >
            Expert IT Support When and Where You Need It
          </Typography>

          <Typography
            variant="h1"
            sx={{
              color: "#FDFBF7",
              fontSize: { xs: "2.5rem", md: "3.75rem" },
              fontFamily: "'Outfit-SemiBold', Helvetica",
              fontWeight: 600,
              lineHeight: 1.2,
              marginBottom: 3,
            }}
          >
            Remote Technical Support & Solutions
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "#B8B5AD",
              fontFamily: "'Inter-Medium', Helvetica",
              fontWeight: 500,
              fontSize: { xs: "1rem", md: "1.25rem" },
              lineHeight: 1.7,
              maxWidth: "560px",
            }}
          >
            Software Installation • Network Setup • Troubleshooting
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroHeroSection;